import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';

/**
 * The Grandsire's crown over the clouds (docs/sendas/druida.md, chapter 4): the grounds, the same from the seed in the
 * sim and the drawing, integers only. Up is −y (the top of the screen: up the crown). Nothing is ground but the wood:
 * every cell off a bough is a rock (cloud below, nobody walks it).
 *
 * - `boughsLayout` (act 1, the boughs): from the bough where they come up to the top of the crown, five boughs as wide
 *   as a road, swinging from side to side, each meeting the next where they fork; snow on them, the boughs above.
 * - `hollowLayout` (act 2, the wood that opens): a hollow of the crown where boughs meet, round and wide; three boughs
 *   going off it into the cloud. Hob down in it with his leg, Ceda at his side.
 * - `sapLayout` (act 3, the test, «La savia de la copa»): the trunk coming up from below, the low crown (the small
 *   tree of the document's example, always: a fork to bud A and to fork H with four buds) and above it the high crown
 *   (one of three shapes, its buds' asks and its knots from the seed, kept only with one or two ways). Hob's hollow off
 *   the trunk's foot.
 *
 * The sap's rule (§4, puzzle 4): at each fork the sap splits in equal parts among the open branches (a branch with its
 * knot closed takes none); 12 come up the trunk. A bud that gets exactly what it asks opens; less, it stays shut; more,
 * it cracks. With the split integer-free: a bud whose path splits by n₁, n₂, … gets 12 / (n₁·n₂·…), so it opens when
 * need · n₁ · n₂ · … = 12, cracks when that is less.
 */

export const CROWN_RADIUS = 10;
export const HOLLOW_RADIUS = 8;
export const SAP_RADIUS = 10;
/** Sap up the trunk each pulse. */
export const SAP_IN = 12;

type Pt = { x: number; y: number };

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

/** The squared distance from a point to a segment (sim units). */
export const segD2 = (x: number, y: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const L2 = dx * dx + dy * dy;
  const k = L2 <= 0 ? 0 : imax(0, imin(1024, idiv(((x - ax) * dx + (y - ay) * dy) * 1024, L2)));
  const px = ax + idiv(dx * k, 1024);
  const py = ay + idiv(dy * k, 1024);
  return (x - px) * (x - px) + (y - py) * (y - py);
};

/** A bough: a segment and how wide (half, sim units). */
export interface Bough {
  a: Pt;
  b: Pt;
  w: number;
}

/** A flat place on the wood: a fork's, a bud's, the hollow (a point and its radius). */
export interface Pad {
  p: Pt;
  r: number;
}

/** Every cell within the board's ground (its radius and two more) that no bough nor pad covers: a rock. */
const woodRocks = (R: number, boughs: Bough[], pads: Pad[]): { rocks: Hex[]; wood: Hex[] } => {
  const rocks: Hex[] = [];
  const wood: Hex[] = [];
  for (const h of allCells(R + 2)) {
    const c = hexToWorld(h.q, h.r);
    let on = false;
    if (hexLen(h.q, h.r) <= R) {
      for (const b of boughs) if (segD2(c.x, c.y, b.a.x, b.a.y, b.b.x, b.b.y) <= b.w * b.w) on = true;
      for (const p of pads) if (!on && (c.x - p.p.x) * (c.x - p.p.x) + (c.y - p.p.y) * (c.y - p.p.y) <= p.r * p.r) on = true;
    }
    (on ? wood : rocks).push(h);
  }
  rocks.sort(sortHex);
  wood.sort(sortHex);
  return { rocks, wood };
};

/* ---------- act 1: the boughs ---------- */

export interface BoughsLayout {
  radius: number;
  rocks: Hex[];
  wood: Hex[];
  boughs: Bough[];
  pads: Pad[];
  /** The way up (sim points): from where they come up, along each bough and across each gap, to the top of the crown. */
  way: Pt[];
  /**
   * Where a bough ends and the next begins with cloud between (`a` the end, `b` the next's start): the cells there are
   * ground only while the wind swings the boughs together.
   */
  gaps: { a: Pt; b: Pt; cells: Hex[] }[];
  start: Pt;
  top: Pt;
  /** Where the crows come down from (the crown's edge, sim points). */
  rim: Pt[];
}

/** The boughs: legs up the crown swinging side to side; a gap of cloud where one ends and the next begins. */
export const BOUGH_LEGS = 7;

export const boughsLayout = (seed: number): BoughsLayout => {
  const R = CROWN_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xb0b5));
  const side = rngInt(rng, 2) === 0 ? -1 : 1;
  const N = BOUGH_LEGS;
  const P: Pt[] = [{ x: -side * 1400, y: 7900 }];
  for (let i = 1; i < N; i++) P.push({ x: (i % 2 === 1 ? side : -side) * rngRange(rng, 3800, 5600), y: 7900 - idiv(15400 * i, N) });
  P.push({ x: side * rngRange(rng, 0, 1200), y: -7500 });
  const boughs: Bough[] = [];
  const pads: Pad[] = [{ p: P[0] as Pt, r: 1500 }];
  const gaps: { a: Pt; b: Pt; cells: Hex[] }[] = [];
  const way: Pt[] = [P[0] as Pt];
  let from = P[0] as Pt;
  for (let i = 1; i <= N; i++) {
    const end = P[i] as Pt;
    boughs.push({ a: from, b: end, w: 950 });
    way.push(end);
    if (i === N) {
      pads.push({ p: end, r: 1900 });
      break;
    }
    // The gap: the next bough starts out over the cloud, toward the leg after.
    const nx = P[i + 1] as Pt;
    const dx = nx.x - end.x;
    const dy = nx.y - end.y;
    const d = imax(1, isqrt2(dx * dx + dy * dy));
    const b = { x: end.x + idiv(dx * 2900, d), y: end.y + idiv(dy * 2900, d) };
    pads.push({ p: end, r: 900 }, { p: b, r: 850 });
    gaps.push({ a: end, b, cells: [] });
    way.push(b);
    from = b;
  }
  // A few dead ends off the boughs: twigs going out a little into the cloud.
  for (let i = 1; i < N; i += 2) {
    const p = P[i] as Pt;
    const out = p.x > 0 ? 1 : -1;
    boughs.push({ a: p, b: { x: p.x + out * rngRange(rng, 1400, 2200), y: p.y + rngRange(rng, 600, 1400) }, w: 620 });
  }
  const all = woodRocks(R, boughs, pads);
  // The gaps' cells: on the line across, clear of either end (they come out of the wood: ground the sim shuts while
  // the boughs are apart).
  const gapCells = new Set<string>();
  for (const g of gaps) {
    const dx = g.b.x - g.a.x;
    const dy = g.b.y - g.a.y;
    const L2 = imax(1, dx * dx + dy * dy);
    for (const h of allCells(R)) {
      const c = hexToWorld(h.q, h.r);
      if (segD2(c.x, c.y, g.a.x, g.a.y, g.b.x, g.b.y) > 760 * 760) continue;
      const k = idiv(((c.x - g.a.x) * dx + (c.y - g.a.y) * dy) * 1000, L2);
      // (Never the cell an end stands in: who waits there must be on wood.)
      if (k <= 170 || k >= 830 || (c.x - g.a.x) * (c.x - g.a.x) + (c.y - g.a.y) * (c.y - g.a.y) < 760 * 760 || (c.x - g.b.x) * (c.x - g.b.x) + (c.y - g.b.y) * (c.y - g.b.y) < 760 * 760) continue;
      g.cells.push(h);
      gapCells.add(`${h.q},${h.r}`);
    }
    g.cells.sort(sortHex);
  }
  const wood = all.wood.filter((h) => !gapCells.has(`${h.q},${h.r}`));
  const rocks = all.rocks.filter((h) => !gapCells.has(`${h.q},${h.r}`));
  const rim: Pt[] = [];
  for (let k = 0; k < 8; k++) rim.push({ x: (k % 2 === 0 ? -1 : 1) * 8600, y: -6000 + k * 1700 });
  return { radius: R, rocks, wood, boughs, pads, way, gaps, start: { ...(P[0] as Pt) }, top: { ...(P[N] as Pt) }, rim };
};

/** An integer square root (layouts only). */
const isqrt2 = (n: number): number => {
  if (n <= 0) return 0;
  let x = n;
  let y = idiv(x + 1, 2);
  while (y < x) {
    x = y;
    y = idiv(x + idiv(n, x), 2);
  }
  return x;
};

/** How far up a way a point is (its nearest point on it, sim units from its start). */
export const wayAlong = (way: readonly Pt[], x: number, y: number): number => {
  let best = 0;
  let bd = -1;
  let acc = 0;
  for (let i = 1; i < way.length; i++) {
    const a = way[i - 1] as Pt;
    const b = way[i] as Pt;
    const L = imax(1, isqrt2((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y)));
    for (let k = 0; k <= 8; k++) {
      const px = a.x + idiv((b.x - a.x) * k, 8);
      const py = a.y + idiv((b.y - a.y) * k, 8);
      const d = (px - x) * (px - x) + (py - y) * (py - y);
      if (bd < 0 || d < bd) {
        bd = d;
        best = acc + idiv(L * k, 8);
      }
    }
    acc += L;
  }
  return best;
};

/** The point a distance up a way. */
export const wayAt = (way: readonly Pt[], d: number): Pt => {
  let acc = 0;
  for (let i = 1; i < way.length; i++) {
    const a = way[i - 1] as Pt;
    const b = way[i] as Pt;
    const L = imax(1, isqrt2((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y)));
    if (acc + L >= d) {
      const k = imax(0, d - acc);
      return { x: a.x + idiv((b.x - a.x) * k, L), y: a.y + idiv((b.y - a.y) * k, L) };
    }
    acc += L;
  }
  return { ...(way[way.length - 1] as Pt) };
};

/* ---------- act 2: the hollow ---------- */

export interface HollowLayout {
  radius: number;
  rocks: Hex[];
  wood: Hex[];
  boughs: Bough[];
  pads: Pad[];
  /** The hollow's middle; Hob down in it, Ceda at his side; the hero. */
  mid: Pt;
  hob: Pt;
  ceda: Pt;
  start: Pt;
}

export const hollowLayout = (seed: number): HollowLayout => {
  const R = HOLLOW_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x4011));
  const mid = { x: 0, y: 0 };
  const boughs: Bough[] = [];
  // Three boughs off it into the cloud, a third of a turn apart, turned by the seed.
  const DX = [1000, 500, -500, -1000, -500, 500];
  const DY = [0, -866, -866, 0, 866, 866];
  const k0 = rngInt(rng, 6);
  for (let i = 0; i < 3; i++) {
    const k = (k0 + i * 2) % 6;
    const L = rngRange(rng, 6200, 7400);
    boughs.push({ a: mid, b: { x: idiv((DX[k] as number) * L, 1000), y: idiv((DY[k] as number) * L, 1000) }, w: 800 });
  }
  const pads: Pad[] = [{ p: mid, r: 4300 }];
  const { rocks, wood } = woodRocks(R, boughs, pads);
  const hob = hexToWorld(-1, 0);
  const ceda = hexToWorld(0, 0);
  return { radius: R, rocks, wood, boughs, pads, mid, hob, ceda, start: hexToWorld(2, 1) };
};

/* ---------- act 3: the sap of the crown ---------- */

/** A node of a crown: a fork (`need` 0) or a bud (what it asks, 1–6), the one below and the ones above, and whether the branch up to it from below has a knot. */
export interface SapNode {
  x: number;
  y: number;
  parent: number;
  kids: number[];
  need: number;
  knot: boolean;
  /** 0: the low crown; 1: the high one. */
  stage: number;
}

export interface SapLayout {
  radius: number;
  rocks: Hex[];
  wood: Hex[];
  boughs: Bough[];
  pads: Pad[];
  nodes: SapNode[];
  /** Each crown's fork the trunk feeds. */
  roots: number[];
  /** The knots: the node whose branch has it, and where it is (the branch's middle). */
  knots: { node: number; x: number; y: number }[];
  /** The trunk's foot, and the ice in it between the crowns. */
  foot: Pt;
  plug: Pt;
  hollow: Pt;
  hob: Pt;
  ceda: Pt;
  start: Pt;
  /** Which of the high crown's shapes, and mirrored. */
  shape: number;
  flip: boolean;
}

/** The high crown's shapes (relative to its fork, up is −y): each node's point, its parent (−1: the fork), a bud or not. */
const SHAPES: readonly { x: number; y: number; p: number; bud: boolean }[][] = [
  // Six buds, two levels: a fork each side, a bud in the middle.
  [
    { x: -3600, y: -2400, p: -1, bud: false },
    { x: 0, y: -3000, p: -1, bud: true },
    { x: 3600, y: -2400, p: -1, bud: false },
    { x: -6000, y: -3800, p: 0, bud: true },
    { x: -3600, y: -5600, p: 0, bud: true },
    { x: 1800, y: -5600, p: 2, bud: true },
    { x: 3800, y: -6200, p: 2, bud: true },
    { x: 6000, y: -4000, p: 2, bud: true },
  ],
  // Seven buds, three levels: a bud left, a fork in the middle with a fork above it, a fork right.
  [
    { x: -5200, y: -2000, p: -1, bud: true },
    { x: 0, y: -2400, p: -1, bud: false },
    { x: 4400, y: -2000, p: -1, bud: false },
    { x: -2200, y: -4600, p: 1, bud: false },
    { x: 1200, y: -5200, p: 1, bud: true },
    { x: -4200, y: -6400, p: 3, bud: true },
    { x: -1400, y: -6800, p: 3, bud: true },
    { x: 3400, y: -5200, p: 2, bud: true },
    { x: 5600, y: -4400, p: 2, bud: true },
    { x: 6800, y: -2400, p: 2, bud: true },
  ],
  // Eight buds, three levels: a fork each side (the left one with a fork above it), a bud in the middle.
  [
    { x: -3800, y: -2200, p: -1, bud: false },
    { x: 0, y: -3200, p: -1, bud: true },
    { x: 3800, y: -2200, p: -1, bud: false },
    { x: -6600, y: -3000, p: 0, bud: true },
    { x: -4800, y: -5200, p: 0, bud: true },
    { x: -2000, y: -4600, p: 0, bud: false },
    { x: -2800, y: -6800, p: 5, bud: true },
    { x: -600, y: -6800, p: 5, bud: true },
    { x: 2000, y: -5400, p: 2, bud: true },
    { x: 4200, y: -6000, p: 2, bud: true },
    { x: 6400, y: -4200, p: 2, bud: true },
  ],
];

/** The low crown (the document's example): fork F, bud A (6, knot), fork H (no knot) with B (3), C (3), D (2), E (4), each with a knot. */
const LOW: readonly { x: number; y: number; p: number; need: number; knot: boolean }[] = [
  { x: -4200, y: -2600, p: -1, need: 6, knot: true },
  { x: 3000, y: -1800, p: -1, need: 0, knot: false },
  { x: 1300, y: -4500, p: 1, need: 3, knot: true },
  { x: 3200, y: -5000, p: 1, need: 3, knot: true },
  { x: 5200, y: -4600, p: 1, need: 2, knot: true },
  { x: 6800, y: -3000, p: 1, need: 4, knot: true },
];

/**
 * Each bud of a crown with these knots closed (by knot index over the whole layout): −1 short, 0 open, 1 cracked (it
 * got more than it asks); forks −2. Integer-free: a bud with the splits n₁·n₂·… on its way gets SAP_IN over that.
 */
export const sapFlow = (nodes: readonly SapNode[], knots: readonly { node: number }[], root: number, closed: readonly boolean[]): number[] => {
  const shut = new Set<number>();
  knots.forEach((k, i) => closed[i] && shut.add(k.node));
  const out = nodes.map(() => -2);
  const walk = (i: number, prod: number, dry: boolean): void => {
    const n = nodes[i] as SapNode;
    if (n.need > 0) {
      const got = dry ? 0 : n.need * prod;
      out[i] = dry || got > SAP_IN ? -1 : got === SAP_IN ? 0 : 1;
      return;
    }
    const open = n.kids.filter((c) => !shut.has(c));
    for (const c of n.kids) walk(c, prod * imax(1, open.length), dry || shut.has(c));
  };
  walk(root, 1, false);
  return out;
};

/** How many of a crown's buds open with these knots closed. */
export const sapOpen = (nodes: readonly SapNode[], knots: readonly { node: number }[], root: number, closed: readonly boolean[]): number => sapFlow(nodes, knots, root, closed).filter((x) => x === 0).length;

/** A crown's ways (every set of its knots closed that opens three buds at once): each a list of knot indices to close. */
export const sapWays = (nodes: readonly SapNode[], knots: readonly { node: number }[], root: number, need: number): number[][] => {
  const mine = knots.map((k, i) => ({ k, i })).filter((x) => (nodes[x.k.node] as SapNode).stage === (nodes[root] as SapNode).stage);
  const out: number[][] = [];
  for (let m = 0; m < 1 << mine.length; m++) {
    const closed = knots.map(() => false);
    const set: number[] = [];
    mine.forEach((x, b) => {
      if (m & (1 << b)) {
        closed[x.i] = true;
        set.push(x.i);
      }
    });
    if (sapOpen(nodes, knots, root, closed) >= need) out.push(set);
  }
  return out;
};

export const sapLayout = (seed: number, need = 3): SapLayout => {
  const R = SAP_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x5a9));
  const flip = rngInt(rng, 2) === 1;
  const fx = (x: number): number => (flip ? -x : x);
  const foot = { x: 0, y: 8400 };
  const lowRoot = { x: 0, y: 5400 };
  const highRoot = { x: 0, y: -900 };
  // The high crown: tried shapes and asks until one with one or two ways (and all open none).
  let best: { nodes: SapNode[]; shape: number } | null = null;
  for (let guard = 0; guard < 400 && !best; guard++) {
    const shape = rngInt(rng, SHAPES.length);
    const sh = SHAPES[shape] as (typeof SHAPES)[number];
    const nodes: SapNode[] = [];
    // The low crown first (its buds' order along H shuffled by the seed).
    nodes.push({ x: fx(lowRoot.x), y: lowRoot.y, parent: -1, kids: [], need: 0, knot: false, stage: 0 });
    const lowNeeds = [3, 3, 2, 4];
    for (let i = lowNeeds.length - 1; i > 0; i--) {
      const j = rngInt(rng, i + 1);
      const t = lowNeeds[i] as number;
      lowNeeds[i] = lowNeeds[j] as number;
      lowNeeds[j] = t;
    }
    LOW.forEach((n, i) => {
      const at = nodes.length;
      const p = n.p < 0 ? 0 : 1 + n.p;
      nodes.push({ x: fx(lowRoot.x + n.x), y: lowRoot.y + n.y, parent: p, kids: [], need: i >= 2 ? (lowNeeds[i - 2] as number) : n.need, knot: n.knot, stage: 0 });
      (nodes[p] as SapNode).kids.push(at);
    });
    const hi = nodes.length;
    nodes.push({ x: fx(highRoot.x), y: highRoot.y, parent: -1, kids: [], need: 0, knot: false, stage: 1 });
    for (const n of sh) {
      const at = nodes.length;
      const p = n.p < 0 ? hi : hi + 1 + n.p;
      // Asks 1–4; a knot on most branches (a few always open).
      nodes.push({ x: fx(highRoot.x + n.x), y: highRoot.y + n.y, parent: p, kids: [], need: n.bud ? 1 + rngInt(rng, 4) : 0, knot: rngInt(rng, 10) < 7, stage: 1 });
      (nodes[p] as SapNode).kids.push(at);
    }
    const knots = nodes.map((n, i) => ({ n, i })).filter((x) => x.n.knot).map((x) => ({ node: x.i }));
    const hk = knots.filter((k) => (nodes[k.node] as SapNode).stage === 1).length;
    if (hk < 4 || hk > 8) continue;
    const ways = sapWays(nodes, knots, hi, need);
    if (ways.length < 1 || ways.length > 2) continue;
    // Not open as it comes, and no way of a single knot (the high crown asks for thinking).
    if (sapOpen(nodes, knots, hi, knots.map(() => false)) >= need) continue;
    if (ways.some((w) => w.length < 2)) continue;
    best = { nodes, shape };
  }
  if (!best) throw new Error('no crown for this seed');
  const nodes = best.nodes;
  const roots = [0, nodes.findIndex((n, i) => i > 0 && n.stage === 1 && n.parent < 0)];
  const knots = nodes
    .map((n, i) => ({ n, i }))
    .filter((x) => x.n.knot)
    .map((x) => {
      const p = nodes[x.n.parent] as SapNode;
      return { node: x.i, x: idiv(p.x + x.n.x, 2), y: idiv(p.y + x.n.y, 2) };
    });
  const boughs: Bough[] = [
    { a: foot, b: { x: fx(lowRoot.x), y: lowRoot.y }, w: 1100 },
    { a: { x: fx(lowRoot.x), y: lowRoot.y }, b: { x: fx(highRoot.x), y: highRoot.y }, w: 1000 },
  ];
  const pads: Pad[] = [{ p: foot, r: 1500 }];
  nodes.forEach((n) => {
    if (n.parent >= 0) {
      const p = nodes[n.parent] as SapNode;
      boughs.push({ a: { x: p.x, y: p.y }, b: { x: n.x, y: n.y }, w: 760 });
    }
    pads.push({ p: { x: n.x, y: n.y }, r: n.need ? 950 : 1150 });
  });
  // Hob's hollow off the trunk's foot.
  const hollow = { x: fx(-5800), y: 6400 };
  boughs.push({ a: hollow, b: { x: fx(-1200), y: 7800 }, w: 820 });
  pads.push({ p: hollow, r: 1500 });
  const { rocks, wood } = woodRocks(R, boughs, pads);
  return {
    radius: R,
    rocks,
    wood,
    boughs,
    pads,
    nodes,
    roots,
    knots,
    foot,
    plug: { x: fx(0), y: idiv(lowRoot.y + highRoot.y, 2) },
    hollow,
    hob: { x: hollow.x + fx(-300), y: hollow.y },
    ceda: { x: hollow.x + fx(600), y: hollow.y - 400 },
    start: { x: fx(700), y: 7600 },
    shape: best.shape,
    flip,
  };
};
